import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Theme Context
 *
 * Provides centralised theme management for the entire application.
 * Supports light and dark themes with system preference detection.
 *
 * This is the SINGLE SOURCE OF TRUTH for theme state. All components
 * should use the useTheme() or useResolvedTheme() hooks to access theme.
 *
 * Features:
 * - Syncs with external DOM changes via MutationObserver
 * - Dispatches 'cfn-theme-changed' events for non-React consumers
 * - Persists preference to localStorage
 * - Supports system preference detection
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
const ThemeContext = createContext(null);
const STORAGE_KEY = 'cfn-theme-preference';
const THEME_CHANGED_EVENT = 'cfn-theme-changed';
/**
 * Get the system's preferred colour scheme
 */
const getSystemTheme = () => {
    if (typeof window === 'undefined')
        return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};
/**
 * Resolve the actual theme based on preference
 */
const resolveTheme = (theme) => {
    if (theme === 'system') {
        return getSystemTheme();
    }
    return theme;
};
/**
 * Load saved theme preference from localStorage
 */
const loadSavedTheme = () => {
    if (typeof window === 'undefined')
        return 'dark';
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'light' || saved === 'dark' || saved === 'system') {
            return saved;
        }
    }
    catch (e) {
        console.warn('Failed to load theme preference:', e);
    }
    // Default to dark theme for CFN Maps
    return 'dark';
};
/**
 * Save theme preference to localStorage
 */
const saveTheme = (theme) => {
    try {
        localStorage.setItem(STORAGE_KEY, theme);
    }
    catch (e) {
        console.warn('Failed to save theme preference:', e);
    }
};
/**
 * Apply theme to the document
 */
const applyTheme = (resolvedTheme) => {
    const root = document.documentElement;
    // Remove existing theme classes
    root.classList.remove('theme-light', 'theme-dark');
    // Add new theme class
    root.classList.add(`theme-${resolvedTheme}`);
    // Update color-scheme for native elements
    root.style.colorScheme = resolvedTheme;
    // Also set data attribute for CSS selectors
    root.dataset.theme = resolvedTheme;
};
/**
 * Get the current theme from DOM classes
 */
const getThemeFromDOM = () => {
    if (document.documentElement.classList.contains('theme-light')) {
        return 'light';
    }
    return 'dark';
};
export const ThemeProvider = ({ children, defaultTheme = 'dark' }) => {
    const [theme, setThemeState] = useState(() => {
        // On initial load, use saved preference or default
        if (typeof window !== 'undefined') {
            return loadSavedTheme();
        }
        return defaultTheme;
    });
    const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(theme));
    // Track if we're the ones making the change (to avoid infinite loops)
    const isInternalChange = useRef(false);
    // Apply theme on mount and when it changes
    useEffect(() => {
        const resolved = resolveTheme(theme);
        setResolvedTheme(resolved);
        isInternalChange.current = true;
        applyTheme(resolved);
        // Reset flag after a tick to allow MutationObserver to ignore this change
        requestAnimationFrame(() => {
            isInternalChange.current = false;
        });
    }, [theme]);
    // Dispatch custom event when resolved theme changes (for non-React consumers)
    useEffect(() => {
        window.dispatchEvent(new CustomEvent(THEME_CHANGED_EVENT, {
            detail: { theme: resolvedTheme }
        }));
    }, [resolvedTheme]);
    // Listen for external DOM changes (e.g., from legacy code)
    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            // Skip if we made this change ourselves
            if (isInternalChange.current)
                return;
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    const domTheme = getThemeFromDOM();
                    if (domTheme !== resolvedTheme) {
                        // External change detected - sync our state
                        setThemeState(domTheme);
                        saveTheme(domTheme);
                    }
                }
            });
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });
        return () => observer.disconnect();
    }, [resolvedTheme]);
    // Listen for system theme changes when using 'system' preference
    useEffect(() => {
        if (theme !== 'system')
            return;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => {
            const newResolved = e.matches ? 'dark' : 'light';
            setResolvedTheme(newResolved);
            isInternalChange.current = true;
            applyTheme(newResolved);
            requestAnimationFrame(() => {
                isInternalChange.current = false;
            });
        };
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);
    const setTheme = useCallback((newTheme) => {
        setThemeState(newTheme);
        saveTheme(newTheme);
    }, []);
    const toggleTheme = useCallback(() => {
        setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
    }, [resolvedTheme, setTheme]);
    const value = {
        theme,
        resolvedTheme,
        setTheme,
        toggleTheme,
    };
    return (_jsx(ThemeContext.Provider, { value: value, children: children }));
};
/**
 * Hook to access theme context
 */
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
/**
 * Hook to get just the resolved theme (for components that only need to read)
 */
export const useResolvedTheme = () => {
    const { resolvedTheme } = useTheme();
    return resolvedTheme;
};
export default ThemeContext;
